import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';

interface RetrySettings {
  maxRetries: number;
  retryDelay: number;
  delayType: 'exponential' | 'linear';
}

class ApiInstance {
  private client: AxiosInstance;

  constructor(baseURL: string, timeout: number) {
    this.client = axios.create({
      baseURL: baseURL,
      timeout: timeout,
    });
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async retryRequest(
    config: AxiosRequestConfig,
    retryCount: number,
    settings: RetrySettings,
  ): Promise<AxiosResponse> {
    try {
      return await this.client(config);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (retryCount < settings.maxRetries) {
        await this.delay(settings.delayType === 'exponential' ? settings.retryDelay * retryCount : settings.retryDelay);
        return this.retryRequest(config, retryCount + 1, settings);
      }
      throw new Error(
        `${error.message}.
${error.response?.data?.title ?? error.response?.data?.message ?? ''}
'${this.client.defaults.baseURL}/${config.url}' API request failed`,
      );
    }
  }

  public getBaseUrl(): string {
    return this.client.defaults.baseURL;
  }

  public getTimeout(): number {
    return this.client.defaults.timeout;
  }

  public async get<T>(
    url: string,
    params: Record<string, unknown>,
    options: {
      bearerToken?: string;
      xSignatureKey?: string;
      settings?: RetrySettings;
    },
  ): Promise<AxiosResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method: 'get',
      params,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    };

    if (options.bearerToken)
      config.headers = {
        Authorization: `Bearer ${options.bearerToken}`,
      };

    if (options.xSignatureKey) {
      config.headers = {
        ...config.headers,
        'x-signature-key': options.xSignatureKey,
      };
    }

    return await this.retryRequest(
      config,
      0,
      options.settings
        ? options.settings
        : {
            maxRetries: 3,
            retryDelay: 5000,
            delayType: 'linear',
          },
    );
  }

  public async post<B, T>(
    url: string,
    body: B,
    options: {
      bearerToken?: string;
      settings?: RetrySettings;
      xSignatureKey?: string;
    },
    params?: Record<string, unknown>,
  ): Promise<AxiosResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method: 'post',
      params,
      data: body,
    };

    if (options.bearerToken)
      config.headers = {
        Authorization: `Bearer ${options.bearerToken}`,
      };

    if (options.xSignatureKey) {
      config.headers = {
        ...config.headers,
        'x-signature-key': options.xSignatureKey,
      };
    }

    return await this.retryRequest(
      config,
      0,
      options.settings
        ? options.settings
        : {
            maxRetries: 0,
            retryDelay: 0,
            delayType: 'linear',
          },
    );
  }

  public async put<B, T>(
    url: string,
    params: Record<string, unknown>,
    body: B,
    options: {
      bearerToken?: string;
      settings?: RetrySettings;
    },
  ): Promise<AxiosResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method: 'put',
      params,
      data: body,
    };

    if (options.bearerToken)
      config.headers = {
        Authorization: `Bearer ${options.bearerToken}`,
      };

    return await this.retryRequest(
      config,
      0,
      options.settings
        ? options.settings
        : {
            maxRetries: 0,
            retryDelay: 0,
            delayType: 'linear',
          },
    );
  }

  public async delete<T>(
    url: string,
    params: Record<string, unknown>,
    options: {
      bearerToken?: string;
      settings?: RetrySettings;
    },
  ): Promise<AxiosResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method: 'delete',
      params,
    };

    if (options.bearerToken)
      config.headers = {
        Authorization: `Bearer ${options.bearerToken}`,
      };

    return await this.retryRequest(
      config,
      0,
      options.settings
        ? options.settings
        : {
            maxRetries: 0,
            retryDelay: 0,
            delayType: 'linear',
          },
    );
  }
}

export { ApiInstance };
export type { RetrySettings };
